import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import { AuthContext } from "../context/AuthContext";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import CommonTable from "../components/CommonTable";

const InventoryUpload = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { user } = useContext(AuthContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [logs, setLogs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fetchLogs = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get(
      `/inventory/upload/stock-upload-logs?organization=${organization}&academicYear=${academicYear}`,
      { headers: { "x-auth-token": token } }
    );
    setLogs(res.data || []);
  };

  useEffect(() => {
    if (organization && academicYear) fetchLogs();
  }, [organization, academicYear]);

  const handleDownloadTemplate = async () => {
    const token = localStorage.getItem("token");
    const url = `/inventory/upload/generate-stock-upload-template?organization=${organization}&academicYear=${academicYear}`;

    try {
      const res = await axios.get(url, {
        responseType: "blob",
        headers: { "x-auth-token": token },
      });

      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);

      // Try to extract filename from Content-Disposition header
      const contentDisposition = res.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || "stock_upload_template.xlsx";

      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Download failed", err);
      showMessage("Download failed", "error");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const confirmed = await confirm({
      title: "Confirm Upload",
      message: "This will update stock records. Continue?",
    });
    if (!confirmed) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("organization", organization);
    formData.append("academicYear", academicYear);

    const token = localStorage.getItem("token");
    try {
      setUploading(true);
      await axios.post("/inventory/upload/upload-stock-template", formData, {
        headers: { "x-auth-token": token },
      });
      showMessage("Stock uploaded successfully", "success");
      fetchLogs();
    } catch (err) {
      showMessage("Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadReconciliation = async (uploadId) => {
    const token = localStorage.getItem("token");
    const url = `/inventory/upload/reconciliation-log-export/${uploadId}`;
    try {
      const res = await axios.get(url, {
        responseType: "blob",
        headers: { "x-auth-token": token },
      });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `reconciliation_log_${uploadId}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      showMessage(
        "Failed to download reconciliation log or no logs were found",
        "error"
      );
    }
  };

  const handleRevert = async (uploadId) => {
    const confirmed = await confirm({
      title: "Revert Upload",
      message: "Are you sure you want to revert this stock upload?",
    });
    if (!confirmed) return;

    const token = localStorage.getItem("token");
    try {
      await axios.post(
        "/inventory/upload/revert-stock-upload",
        { uploadId },
        { headers: { "x-auth-token": token } }
      );
      showMessage("Upload reverted successfully", "success");
      fetchLogs();
    } catch (err) {
      showMessage("Revert failed", "error");
    }
  };

  const latestUpload = logs.find((l) => !l.revertedAt);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Stock Upload
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={handleDownloadTemplate}>
          Download Template
        </Button>
        <Button variant="contained" component="label" disabled={uploading}>
          {uploading ? <CircularProgress size={20} /> : "Upload Stock"}
          <input type="file" hidden accept=".xlsx" onChange={handleUpload} />
        </Button>
      </Stack>

      <CommonTable
        rows={logs.map((log) => ({
          uploadId: log.uploadId,
          uploadedBy: log.uploadedBy?.name || "Unknown",
          createdAt: log.createdAt,
          status: log.revertedAt ? "Reverted" : "Uploaded",
          revertedAt: log.revertedAt,
          revertedBy: log.revertedBy?.name,
        }))}
        columns={[
          {
            label: "Uploaded On",
            renderCell: (row) =>
              new Date(row.createdAt).toLocaleString("en-IN"),
          },
          {
            label: "Uploaded By",
            field: "uploadedBy",
          },
          { label: "Status", field: "status" },
          {
            label: "Reverted On",
            renderCell: (row) =>
              row.revertedAt
                ? new Date(row.revertedAt).toLocaleString("en-IN")
                : "-",
          },
          { label: "Reverted By", field: "revertedBy" },
          {
            label: "Action",
            renderCell: (row) => (
              <Stack direction="row" spacing={1}>
                {row.status === "Uploaded" &&
                  row.uploadId === latestUpload?.uploadId && (
                    <Button
                      size="small"
                      onClick={() => handleRevert(row.uploadId)}
                      color="error"
                    >
                      Revert
                    </Button>
                  )}
                {row.status === "Uploaded" && (
                  <Button
                    size="small"
                    onClick={() => handleDownloadReconciliation(row.uploadId)}
                  >
                    View Diff Log
                  </Button>
                )}
              </Stack>
            ),
          },
        ]}
      />
    </Box>
  );
};

export default InventoryUpload;
